type Coordinate = [number, number]; // [lng, lat]
type Vector = { x: number; y: number };

/** Lines within this many degrees of the start → target direction may set the bearing. */
export const MAX_LINE_ANGLE = 30;
/** Added to a line's angle difference when weighting, so a near-perfectly aligned short line can't dominate. */
const ANGLE_SOFTENING = 5;

const normalize180 = (degrees: number) => ((((degrees + 180) % 360) + 360) % 360) - 180;
const bearingTo = ({ x, y }: Vector) => (Math.atan2(x, y) * 180) / Math.PI;

/**
 * Map bearing for the full route overview: the start at the bottom of the screen, the route going up.
 *
 * Starts from the start → target direction, where the target is the average of the route's bounding-box
 * center and every stop after the start (the end of each leg). Then snaps to the route's best-aligned
 * straight line within `MAX_LINE_ANGLE` of it, weighted by length / (angle difference + 5°). The route's
 * first line counts triple. Lines are directionless, so each is taken in the orientation facing the route.
 *
 * @param segmentsPerLeg - one array of navline segments per route leg, all floors
 */
export const getOverviewBearing = (segmentsPerLeg: { coordinates: Coordinate[] }[][]): number => {
  const segments = segmentsPerLeg.flat();
  const start = segments.find(segment => segment.coordinates.length)?.coordinates[0];
  if (!start) {
    return 0;
  }
  // Local x (east) / y (north) around the start; flat-earth is accurate enough at venue scale.
  const cosLat = Math.cos((start[1] * Math.PI) / 180);
  const toLocal = ([lng, lat]: Coordinate): Vector => ({ x: (lng - start[0]) * cosLat, y: lat - start[1] });
  const lines = segments.map(({ coordinates }) => coordinates.map(toLocal));
  const points = lines.flat();
  const xs = points.map(({ x }) => x);
  const ys = points.map(({ y }) => y);
  const center = { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
  const stops = segmentsPerLeg.flatMap(leg => leg.flatMap(({ coordinates }) => coordinates).slice(-1)).map(toLocal);
  const targets = [center, ...stops];
  const main = bearingTo({
    x: targets.reduce((sum, { x }) => sum + x, 0) / targets.length,
    y: targets.reduce((sum, { y }) => sum + y, 0) / targets.length,
  });

  let best = { bearing: main, weight: 0 };
  lines.forEach((line, lineIndex) =>
    line.slice(1).forEach((b, i) => {
      const a = line[i];
      const length = Math.hypot(b.x - a.x, b.y - a.y);
      const along = normalize180(bearingTo({ x: b.x - a.x, y: b.y - a.y }) - main);
      const offset = Math.abs(along) > 90 ? normalize180(along + 180) : along; // face the route, not away
      const weight = (length / (Math.abs(offset) + ANGLE_SOFTENING)) * (lineIndex === 0 && i === 0 ? 3 : 1);
      if (length > 0 && Math.abs(offset) <= MAX_LINE_ANGLE && weight > best.weight) {
        best = { bearing: main + offset, weight };
      }
    }),
  );

  // Keep the start in the lower half: the route must reach at least as far ahead of it (screen-up) as behind.
  const radians = (best.bearing * Math.PI) / 180;
  const ahead = points.map(({ x, y }) => x * Math.sin(radians) + y * Math.cos(radians));
  return normalize180(Math.min(...ahead) + Math.max(...ahead) >= 0 ? best.bearing : main);
};
